import { translationKey } from '../i18n';
import { getInternationalMedicationTips } from '../utils/internationalMedicationTips';
import useAppLanguage, { localize, readAppLanguage } from '../i18n';
import { displayChatMessage } from '../utils/chatMessageCopy';
import useNavigate from '../hooks/useSafeNavigate';
import useAiDataConsent from '../hooks/useAiDataConsent';
import DialogTitle from '../components/ui/FitDialogTitle';
import SingleLineText from '../components/ui/SingleLineText';
import Button from '../components/ui/FitButton';
import Dialog from '../components/StableDialog';
import LoadingLabel from '../components/LoadingLabel';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Box, Typography, TextField, IconButton, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import InfoIcon from '@mui/icons-material/Info';

import AppToast from '../components/AppToast';
import { getDeviceUuid, sendChatMessage, claimChatRewardBonus, reportChatMessage } from '../api';
import WebviewMessenger from '../utils/WebviewMessenger';
import { requestAd, rewardedAdFailureMessage } from '../utils/adRequests';
import useKeyboard from '../hooks/useKeyboard';
import useRequestGuard from '../hooks/useRequestGuard';
import { getScreenCacheContextRevision } from '../utils/localCache';

const SOURCE_DRUG_INFO_URL = 'https://nedrug.mfds.go.kr';

const SUGGESTIONS = [
    translationKey('이 약 술 마셔도 돼?'),
    translationKey('부작용이 뭐야?'),
    translationKey('식전에 먹어야 해?'),
    translationKey('같이 먹으면 안 되는 음식 있어?'),
];

const LOADING_STEPS = [
    translationKey('등록하신 약 확인'),
    translationKey('안전성 검토 중...'),
    translationKey('답변 정리'),
];

const FUN_FACTS = [
    { icon: '💡', text: translationKey('약 봉투의 "돈복(頓服)"은 한 번에 다 드시라는 뜻이에요.') },
    { icon: '💧', text: translationKey('약은 미지근한 물 한 컵(약 200ml)으로 드셔야 잘 녹아요.') },
    { icon: '🥛', text: translationKey('철분제는 우유와 같이 먹으면 흡수율이 절반으로 떨어져요.') },
    { icon: '☕', text: '카페인 음료로 약을 드시면 흡수와 효과가 달라질 수 있어요.' },
    { icon: '🍇', text: '자몽주스는 80여 종의 약과 상호작용해요. 따로 드시는 게 안전해요.' },
    { icon: '🌡️', text: '약은 직사광선과 습기를 싫어해요. 욕실은 피하고 서랍 보관이 좋아요.' },
    { icon: '🍽️', text: '"식후 30분" 권장은 위에 음식이 있어야 위 점막 자극이 덜하기 때문이에요.' },
    { icon: '💤', text: '감기약의 졸음 성분은 보통 "클로르페니라민"이에요. 운전 전엔 확인해보세요.' },
    { icon: '🧪', text: '같은 성분이라도 알약, 시럽, 캡슐마다 흡수 속도가 달라요.' },
    { icon: '⏰', text: '"하루 3번" 약은 보통 8시간 간격이 가장 좋아요.' },
    { icon: '🚫', text: '쓴 약은 입천장 대신 혀 뒤쪽으로 삼키면 쓴맛이 덜해요.' },
    { icon: '🍷', text: '타이레놀과 술은 간에 동시 부담을 줘요. 24시간 간격이 안전해요.' },
    { icon: '📌', text: '항생제는 정해진 기간 동안 끝까지 복용해야 내성균이 안 생겨요.' },
    { icon: '📅', text: '유효기간이 지난 약은 효과가 떨어지고, 일부는 변질될 수 있어요.' },
    { icon: '🤝', text: '여러 약을 동시에 드시면 약사·의사에게 꼭 알려주세요.' },
];

const INITIAL_MESSAGE = { role: 'assistant', content: translationKey('안녕하세요! 약에 대해 궁금한 점을 물어보세요.\n\n등록하신 약 정보를 바탕으로 맞춤 상담해드릴게요.') };
const CHAT_STORAGE_KEY = 'ai_chat_history';

function loadChatHistory() {
    try {
        const stored = JSON.parse(localStorage.getItem(CHAT_STORAGE_KEY) || '{}');
        const today = new Date().toISOString().slice(0, 10);
        if (stored.ownerUuid === getDeviceUuid() && stored.date === today && stored.messages?.length > 0) {
            return stored.messages;
        }
    } catch {}
    return [INITIAL_MESSAGE];
}

function saveChatHistory(messages) {
    try {
        const today = new Date().toISOString().slice(0, 10);
        localStorage.setItem(CHAT_STORAGE_KEY, JSON.stringify({ ownerUuid: getDeviceUuid(), date: today, messages }));
    } catch {}
}

// 우측 헤더 장식용 노랑 알약 SVG
function PharmacistDecor({ size = 22 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
            <rect x="3" y="9" width="18" height="6" rx="3" fill="#ffe566" />
            <rect x="3" y="9" width="9" height="6" rx="3" fill="#e8d040" />
            <rect x="3" y="9" width="18" height="6" rx="3" stroke="#c8b020" strokeWidth="0.8" />
        </svg>
    );
}

// 답변 대기 중 보여주는 단계 인디케이터 카드.
// 단계 진행(0→2→4초)은 LoadingBubble에서 관리하고 step을 prop으로 받음.
function StepIndicatorCard({ step }) {
    return (
        <Box sx={{
            maxWidth: '88%', alignSelf: 'flex-start',
            bgcolor: '#fff', borderRadius: '18px 18px 18px 4px',
            borderLeft: '3px solid #ffe566',
            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
            p: '14px 16px',
            animation: 'msgIn 0.25s ease-out',
            '@keyframes msgIn': {
                from: { opacity: 0, transform: 'translateY(8px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
            },
            '@keyframes dotPulse': {
                '0%, 100%': { transform: 'scale(1)' },
                '50%': { transform: 'scale(1.15)' },
            },
        }}>
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                fontSize: '0.875rem', fontWeight: 700, color: '#1a1a1a',
                mb: 1.5,
            }}>
                <span style={{ fontSize: '1rem' }}>💊</span>{localize("약사 AI가 살펴보고 있어요")}</Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
                {LOADING_STEPS.map((label, i) => {
                    const isDone = i < step;
                    const isActive = i === step;
                    const color = isDone ? '#666' : isActive ? '#1a1a1a' : '#bbb';
                    const weight = isActive ? 600 : 500;
                    return (
                        <Box key={i} sx={{
                            display: 'flex', alignItems: 'center', gap: '10px',
                            fontSize: '0.84rem', color, fontWeight: weight,
                            transition: 'color 0.3s ease',
                        }}>
                            {isDone ? (
                                <Box component="span" sx={{
                                    width: 18, height: 18, borderRadius: '50%',
                                    bgcolor: '#1F8A3D', display: 'inline-flex',
                                    alignItems: 'center', justifyContent: 'center',
                                    color: '#fff', fontSize: '0.7rem', fontWeight: 800,
                                }}>✓</Box>
                            ) : isActive ? (
                                <Box component="span" sx={{
                                    width: 18, height: 18, borderRadius: '50%',
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <Box component="span" sx={{
                                        width: 10, height: 10, borderRadius: '50%',
                                        bgcolor: '#ffe566',
                                        animation: 'dotPulse 1.2s ease-in-out infinite',
                                        boxShadow: '0 0 0 4px rgba(255,229,102,0.25)',
                                    }} />
                                </Box>
                            ) : (
                                <Box component="span" sx={{
                                    width: 18, height: 18, borderRadius: '50%',
                                    border: '1.5px solid #ddd', display: 'inline-block',
                                }} />
                            )}
                            <span>{localize(label)}</span>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

// 답변 대기 중 회전하는 약 상식 카드. tipIndex가 바뀌면 fade out → in 트랜지션.
export function TipCard({ tipIndex, tips }) {
    const [displayIndex, setDisplayIndex] = useState(tipIndex);
    const [fadeState, setFadeState] = useState('in');

    useEffect(() => {
        if (tipIndex !== displayIndex) {
            setFadeState('out');
            const t = setTimeout(() => {
                setDisplayIndex(tipIndex);
                setFadeState('in');
            }, 400);
            return () => clearTimeout(t);
        }
    }, [tipIndex, displayIndex]);

    const tip = tips[displayIndex % tips.length];

    return (
        <Box sx={{
            maxWidth: '88%', alignSelf: 'flex-start',
            bgcolor: '#fffbe6', border: '1px solid #f5e7a0',
            borderRadius: 4, p: '14px 16px',
            animation: 'msgIn 0.25s ease-out 0.08s both',
        }}>
            <Box sx={{
                opacity: fadeState === 'in' ? 1 : 0,
                transform: fadeState === 'in' ? 'translateY(0)' : 'translateY(8px)',
                transition: 'opacity 0.4s ease, transform 0.4s ease',
            }}>
                <Typography sx={{
                    fontSize: '0.82rem', fontWeight: 700, color: '#8a7000', mb: 0.5,
                }}>
                    {tip.icon}{localize(" 알고 계셨나요?")}</Typography>
                <Typography sx={{ fontSize: '0.86rem', color: '#5a4a00', lineHeight: 1.55 }}>
                    {tip.text}
                </Typography>
            </Box>
        </Box>
    );
}

// 로딩 영역 — 단계 인디케이터 카드 + 약 상식 카드.
// 자체적으로 step(0→1→2)과 tipIndex(4초 간격 순환)를 타이머로 관리한다.
export function LoadingBubble() {
    const { language } = useAppLanguage();
    const tips = language === 'ko' ? FUN_FACTS : getInternationalMedicationTips();
    const [step, setStep] = useState(0);
    // 매 세션마다 다른 카드부터 보이도록 랜덤 시작 인덱스.
    const [tipIndex, setTipIndex] = useState(() => Math.floor(Math.random() * tips.length));

    useEffect(() => {
        const t1 = setTimeout(() => setStep(1), 2000);
        const t2 = setTimeout(() => setStep(2), 4000);
        const tipInterval = setInterval(() => {
            setTipIndex(i => (i + 1) % tips.length);
        }, 4000);
        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            clearInterval(tipInterval);
        };
    }, [tips.length]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2, alignItems: 'flex-start', mb: 1.5 }}>
            <StepIndicatorCard step={step} />
            {<TipCard tipIndex={tipIndex} tips={tips} />}
        </Box>
    );
}

export default function ChatPage() {
    const { language } = useAppLanguage();
    const { requestAiConsent, aiConsentDialog, aiConsentPending } = useAiDataConsent('chat');
    const beginRequest = useRequestGuard('ai_chat');
    const historyOwnerRevision = useRef(getScreenCacheContextRevision());
    useEffect(() => {
        const reset = () => {
            historyOwnerRevision.current = getScreenCacheContextRevision();
            setMessages([INITIAL_MESSAGE]); setInput(''); setLoading(false);
            setLimitReached(false); setRewardAvailable(true); setRewardAmount(3);
            setReportedSet(new Set()); setReportDialog({ open: false, content: '' }); setReportSubmitting(false);
        };
        window.addEventListener('kkomok-account-context-changed', reset);
        return () => window.removeEventListener('kkomok-account-context-changed', reset);
    }, []);
    const navigate = useNavigate();
    const [messages, setMessages] = useState(loadChatHistory);
    const [input, setInput] = useState('');
    const [loading, setLoading] = useState(false);
    const [limitReached, setLimitReached] = useState(false);
    const [rewardAvailable, setRewardAvailable] = useState(true);
    const [rewardAmount, setRewardAmount] = useState(3);
    const [reportedSet, setReportedSet] = useState(() => new Set());
    const [reportDialog, setReportDialog] = useState({ open: false, content: '' });
    const [reportSubmitting, setReportSubmitting] = useState(false);
    const [reportSnack, setReportSnack] = useState({ open: false, message: '', severity: 'success' });
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);
    const isNativeIOSApp = typeof window !== 'undefined' && window.IS_NATIVE_APP && window.NATIVE_PLATFORM === 'ios';
    // WebView 바깥 RN KeyboardAvoidingView를 쓰지 않고, visualViewport 하나만 기준으로
    // 높이를 맞춰 iOS WKWebView의 focus auto-scroll과 이중 보정이 겹치지 않게 한다.
    const { viewportHeight } = useKeyboard();

    const openReportDialog = (content) => {
        if (!content || reportedSet.has(content)) return;
        setReportDialog({ open: true, content });
    };

    const closeReportDialog = () => {
        if (reportSubmitting) return;
        setReportDialog({ open: false, content: '' });
    };

    const submitReport = async () => {
        const { content } = reportDialog;
        if (!content) return;
        setReportSubmitting(true);
        const isCurrent = beginRequest('report');
        try {
            const uuid = getDeviceUuid();
            await reportChatMessage(uuid, content);
            if (!isCurrent()) return;
            setReportedSet(prev => new Set([...prev, content]));
            setReportDialog({ open: false, content: '' });
            setReportSnack({ open: true, message: localize("신고가 접수되었어요. 검토하여 개선하겠습니다."), severity: 'success' });
        } catch {
            if (!isCurrent()) return;
            setReportSnack({ open: true, message: localize("신고 처리 중 오류가 발생했어요. 잠시 후 다시 시도해주세요."), severity: 'error' });
        } finally {
            if (isCurrent()) setReportSubmitting(false);
        }
    };

    const handleRewardComplete = useCallback(async () => {
        const isCurrent = beginRequest('reward');
        try {
            const uuid = getDeviceUuid();
            const res = await claimChatRewardBonus(uuid);
            if (!isCurrent()) return;
            if (res.success) {
                setLimitReached(false);
                setMessages(prev => [...prev, { role: 'assistant', content: localize("상담 {v0}회가 추가되었어요! 계속 질문해주세요 😊", { v0: res.remaining }) }]);
            } else {
                if (res.rewardAvailable === false) setRewardAvailable(false);
                setMessages(prev => [...prev, { role: 'assistant', content: res.message || localize('보너스를 받을 수 없어요.') }]);
            }
        } catch {
            if (!isCurrent()) return;
            setMessages(prev => [...prev, { role: 'assistant', content: localize('보너스 처리 중 오류가 발생했어요.') }]);
        }
    }, [beginRequest]);

    useEffect(() => {
        const handler = (event) => {
            try {
                const data = JSON.parse(event.data);
                const feature = data.feature || 'ai_chat_reward';
                if (feature !== 'ai_chat_reward') return;
                if (data.type === 'REWARDED_AD_COMPLETE') handleRewardComplete();
                if (data.type === 'REWARDED_AD_UNAVAILABLE') {
                    setMessages(prev => [...prev, { role: 'assistant', content: rewardedAdFailureMessage(data.reason) }]);
                }
            } catch {}
        };
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, [handleRewardComplete]);

    useEffect(() => {
        // block:'nearest' — viewport scroll까지 일으키지 않도록 (fixed 컨테이너 시프트 회피).
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, [messages, loading]);

    // 메시지 변경 시 localStorage에 저장
    useEffect(() => {
        if (messages.length > 1 && historyOwnerRevision.current === getScreenCacheContextRevision()) {
            saveChatHistory(messages);
        }
    }, [messages]);

    const requestRewardedAd = () => {
        requestAd('rewarded', 'ai_chat_reward');
    };

    const handleSend = async (text) => {
        const msg = text || input.trim();
        if (!msg || loading || aiConsentPending) return;
        const isCurrent = beginRequest('send');
        document.activeElement?.blur?.();
        if (!await requestAiConsent() || !isCurrent()) return;

        // 키보드 떠 있을 때 fixed 컨테이너가 viewport 위로 밀려나는 iOS WebView 버그 회피.
        // 메시지 전송 시점에 키보드를 명시적으로 내림.
        document.activeElement?.blur?.();

        const userMsg = { role: 'user', content: msg };
        const newMessages = [...messages, userMsg];
        setMessages(newMessages);
        setInput('');
        setLoading(true);

        try {
            const uuid = getDeviceUuid();
            // Send history excluding the initial greeting
            const history = messages.filter((_, i) => i > 0).map(m => ({ role: m.role, content: m.content }));
            const res = await sendChatMessage(uuid, msg, history);
            if (!isCurrent()) return;
            setLimitReached(false);
            setMessages([...newMessages, { role: 'assistant', content: res.reply, aiGenerated: true }]);
        } catch (err) {
            if (!isCurrent()) return;
            if (err.response?.status === 429) {
                const available = err.response.data?.rewardAvailable !== false;
                const amount = err.response.data?.rewardAmount || 3;
                setLimitReached(true);
                setRewardAvailable(available);
                setRewardAmount(amount);
                setMessages([...newMessages, { role: 'assistant', content: available
                    ? localize("오늘 이용 가능한 상담 횟수를 모두 사용했어요.\n광고를 보면 {v0}회 추가할 수 있어요.", { v0: amount })
                    : localize('오늘 상담 100회를 모두 사용했어요.\n내일 다시 이용해주세요.') }]);
            } else {
                setMessages([...newMessages, { role: 'assistant', content: localize('오류가 발생했어요. 다시 시도해주세요.') }]);
            }
        } finally {
            if (isCurrent()) setLoading(false);
        }
    };

    return (
        <Box sx={{
            position: 'fixed',
            top: 0,
            left: '50%', transform: 'translateX(-50%)',
            width: '100%', maxWidth: 480,
            height: `${viewportHeight}px`,
            boxSizing: 'border-box',
            display: 'flex', flexDirection: 'column', bgcolor: '#f9fafb',
            '@keyframes msgIn': {
                from: { opacity: 0, transform: 'translateY(8px)' },
                to: { opacity: 1, transform: 'translateY(0)' },
            },
        }}>
            {/* Header — 좌: 뒤로가기/타이틀, 우: 노랑 알약 장식 */}
            <Box sx={{
                pt: '8px',
                pb: 1, px: 1, bgcolor: '#fff',
                borderBottom: '1px solid #f0f0f0', flexShrink: 0,
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.2, ...(language === 'ko' ? {} : { flex: 1, minWidth: 0 }) }}>
                        <IconButton
                            aria-label={localize("뒤로가기")}
                            onClick={() => navigate(-1)}
                            sx={{ color: '#1a1a1a', width: 48, height: 48, p: 0, ml: -0.5, flexShrink: 0 }}
                        >
                            <ArrowBackIcon />
                        </IconButton>
                        <Box>
                            <SingleLineText data-chat-heading sx={{ fontSize: '1.2rem', fontWeight: 800, color: '#1a1a1a', lineHeight: 1.2 }}>{localize("AI 약 상담")}</SingleLineText>
                            <SingleLineText data-chat-heading sx={{ fontSize: '0.82rem', fontWeight: 500, color: '#777', mt: '1px' }}>{localize("내 약 정보 기반 맞춤 상담")}</SingleLineText>
                        </Box>
                    </Box>
                    <Box sx={{
                        width: 36, height: 36, borderRadius: '50%', bgcolor: '#fffbe6',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        mr: 1, flexShrink: 0,
                    }}>
                        <PharmacistDecor size={22} />
                    </Box>
                </Box>
            </Box>

            {/* AI 면책 배너 - 정책 고지, 항상 노출 */}
            <Box sx={{
                px: 2, py: 1, bgcolor: '#fffbe6', borderBottom: '1px solid #f5e7a0',
                flexShrink: 0,
            }}>
                <Box sx={{
                    display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                    gap: 0.6,
                }}>
                    <InfoIcon sx={{ fontSize: 14, color: '#8a7000', mt: '2px', flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '0.82rem', color: '#8a7000', textAlign: 'center', lineHeight: 1.4 }}>{localize("AI 답변은 참고용이에요 · 의료 결정은 의사·약사와 상담하세요")}</Typography>
                </Box>
                <Typography sx={{ fontSize: '0.72rem', color: '#8a7000', textAlign: 'center', lineHeight: 1.4, mt: 0.3 }}>{localize("정확한 의약품 정보:")}{' '}
                    <Box
                        component="span"
                        role="button"
                        tabIndex={0}
                        onClick={() => WebviewMessenger.openExternalUrl(SOURCE_DRUG_INFO_URL)}
                        sx={{
                            color: '#5a4a00', fontWeight: 700, textDecoration: 'underline',
                            cursor: 'pointer', userSelect: 'none',
                        }}
                    >{localize("식약처 의약품안전나라")}</Box>
                </Typography>
            </Box>

            {/* Messages */}
            <Box sx={{ flex: 1, overflow: 'auto', px: '14px', py: 2, display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                {messages.map((msg, idx) => {
                    const isUser = msg.role === 'user';
                    const isAI = !!msg.aiGenerated;
                    return (
                        <Box
                            key={idx}
                            sx={{
                                display: 'flex',
                                justifyContent: isUser ? 'flex-end' : 'flex-start',
                                animation: idx === messages.length - 1 ? 'msgIn 0.25s ease-out' : undefined,
                            }}
                        >
                            <Box
                                sx={{
                                    maxWidth: isUser ? '78%' : '88%',
                                    px: '14px', py: isAI ? '12px' : '11px',
                                    borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                                    bgcolor: isUser ? '#ffe566' : '#fff',
                                    color: isUser ? '#5a4a00' : '#1a1a1a',
                                    boxShadow: isUser ? 'none' : '0 1px 4px rgba(0,0,0,0.06)',
                                    borderLeft: !isUser ? '3px solid #ffe566' : undefined,
                                    fontSize: '0.94rem',
                                    lineHeight: 1.6,
                                    whiteSpace: 'pre-wrap',
                                    fontWeight: isUser ? 500 : 400,
                                }}
                            >
                                {isAI && (
                                    <Box component="span" sx={{
                                        display: 'inline-block',
                                        fontSize: '0.66rem', fontWeight: 600, color: '#8a7000',
                                        bgcolor: '#fef9e0', borderRadius: '6px',
                                        px: '7px', py: '2px', mb: 1,
                                        letterSpacing: '0.02em',
                                    }}>{localize("AI 생성")}</Box>
                                )}
                                <Box>
                                    {displayChatMessage(msg, INITIAL_MESSAGE.content).split(/(\*\*[^*]+\*\*)/).map((part, i) =>
                                        part.startsWith('**') && part.endsWith('**')
                                            ? <strong key={i}>{part.slice(2, -2)}</strong>
                                            : <React.Fragment key={i}>{part}</React.Fragment>
                                    )}
                                </Box>
                                {isAI && (
                                    <Box sx={{
                                        mt: 1.2, pt: 1, borderTop: '1px dashed #e8e8e8',
                                        fontSize: '0.72rem', color: '#888', lineHeight: 1.55,
                                    }}>
                                        <Box>{localize("📚 정확한 의약품 정보:")}{' '}
                                            <Box
                                                component="span"
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => WebviewMessenger.openExternalUrl(SOURCE_DRUG_INFO_URL)}
                                                sx={{
                                                    color: '#5a4a00', fontWeight: 700,
                                                    textDecoration: 'underline', cursor: 'pointer',
                                                    userSelect: 'none',
                                                }}
                                            >{localize("식약처 의약품안전나라")}</Box>
                                        </Box>
                                        <SingleLineText component="div" variant="inherit" sx={{ mt: 0.2, ...(readAppLanguage() !== 'ko' && { whiteSpace: 'normal', overflowWrap: 'anywhere' }) }}>{localize("약물 안전성 결정은 반드시 의사·약사와 상담하세요")}</SingleLineText>
                                    </Box>
                                )}
                                {isAI && (
                                    <Box sx={{ mt: 0.8, display: 'flex', justifyContent: 'flex-end' }}>
                                        <Box
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => openReportDialog(msg.content)}
                                            sx={{
                                                display: 'inline-flex', alignItems: 'center', gap: 0.3,
                                                fontSize: '0.7rem',
                                                color: reportedSet.has(msg.content) ? '#ddd' : '#bbb',
                                                cursor: reportedSet.has(msg.content) ? 'default' : 'pointer',
                                                userSelect: 'none', py: '3px', px: '6px',
                                                pointerEvents: reportedSet.has(msg.content) ? 'none' : 'auto',
                                                '&:active': { color: '#666' },
                                            }}
                                        >
                                            <FlagOutlinedIcon sx={{ fontSize: '0.85rem' }} />
                                            <span>{reportedSet.has(msg.content) ? localize("신고됨") : localize("신고")}</span>
                                        </Box>
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    );
                })}

                {loading && <LoadingBubble />}

                {limitReached && rewardAvailable && window.ReactNativeWebView && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
                        <Button
                            variant="contained"
                            startIcon={<PlayCircleIcon />}
                            onClick={requestRewardedAd}
                            sx={{
                                bgcolor: '#ffe566', color: '#5a4a00', borderRadius: 4,
                                px: '26px', py: '13px', fontWeight: 700, fontSize: '0.97rem',
                                boxShadow: '0 4px 12px rgba(230,168,0,0.3)',
                                '&:hover': { bgcolor: '#e8d040' },
                            }}
                        >{localize("광고 보고 {v0}회 추가하기", { v0: rewardAmount })}</Button>
                    </Box>
                )}

                <div ref={messagesEndRef} />

            </Box>

            {/* Suggestions - always visible above input */}
            <Box sx={{
                px: '14px', py: '10px', bgcolor: 'white', borderTop: '1px solid #f0f0f0',
                display: 'flex', gap: 1, overflowX: 'auto', flexShrink: 0,
                '&::-webkit-scrollbar': { display: 'none' },
            }}>
                {SUGGESTIONS.map((s) => (
                    <Box
                        key={localize(s)}
                        role="button"
                        tabIndex={0}
                        onClick={() => !loading && handleSend(localize(s))}
                        sx={{
                            px: '14px', py: '8px', borderRadius: 20, whiteSpace: 'nowrap',
                            border: '1px solid #e5e7eb', bgcolor: '#fff',
                            fontSize: '0.84rem', color: '#444', fontWeight: 500,
                            cursor: loading ? 'default' : 'pointer', userSelect: 'none', flexShrink: 0,
                            opacity: loading ? 0.5 : 1,
                            transition: 'all 0.15s ease',
                            '&:active': { bgcolor: '#fffbe6', borderColor: '#f5e7a0', transform: 'scale(0.97)' },
                        }}
                    >
                        {localize(s)}
                    </Box>
                ))}
            </Box>

            {aiConsentDialog}

            {/* Input — 상단 여백(py:1.5)과 동일한 12px 고정. BottomNav는 /chat에서 숨김 처리 */}
            <Box sx={{
                px: '14px', pt: '10px', pb: '14px', bgcolor: 'white',
                borderTop: '1px solid #f0f0f0',
                display: 'flex', gap: 1, alignItems: 'flex-end',
                flexShrink: 0,
            }}>
                <TextField
                    ref={inputRef}
                    fullWidth
                    multiline
                    maxRows={3}
                    placeholder={localize("약에 대해 물어보세요")}
                    value={input}
                    onChange={(e) => setInput(e.target.value.slice(0, 500))}
                    onKeyDown={(e) => {
                        // 한글 IME composition 중에는 Enter를 자모 확정 신호로 두고 send하지 않음.
                        if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                            e.preventDefault();
                            handleSend();
                        }
                    }}
                    size="small"
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 4,
                            bgcolor: '#f5f5f5',
                            fontSize: isNativeIOSApp ? '16px' : '0.94rem',
                            '& fieldset': { border: 'none' },
                        },
                        '& .MuiInputBase-input': {
                            fontSize: isNativeIOSApp ? '16px' : '0.94rem',
                        },
                    }}
                />
                <IconButton
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSend()}
                    disabled={!input.trim() || loading}
                    sx={{
                        bgcolor: input.trim() && !loading ? '#ffe566' : '#f5f5f5',
                        color: input.trim() && !loading ? '#5a4a00' : '#bbb',
                        '&:hover': { bgcolor: input.trim() && !loading ? '#e8d040' : '#f5f5f5' },
                        '&.Mui-disabled': { bgcolor: '#f5f5f5', color: '#bbb' },
                        width: 40, height: 40,
                        transition: 'background 0.15s ease',
                    }}
                >
                    <SendIcon sx={{ fontSize: 20 }} />
                </IconButton>
            </Box>

            {/* 답변 신고 다이얼로그 */}
            <Dialog open={reportDialog.open} onClose={closeReportDialog}
                PaperProps={{ sx: { borderRadius: 4, width: '100%', maxWidth: 340, mx: 3 } }}>
                <DialogTitle sx={{ fontSize: '1.1rem', fontWeight: 700, color: '#1a1a1a', px: 2.5, pt: 2.5, pb: 1 }}>{localize("이 답변을 신고할까요?")}</DialogTitle>
                <DialogContent sx={{ px: 2.5, pb: 0 }}>
                    <DialogContentText sx={{ fontSize: '0.91rem', color: '#666', lineHeight: 1.55 }}>{localize("부정확하거나 의료적으로 위험할 수 있는 답변을 운영자에게 알려요.")}</DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 2.5, pt: 2.5, pb: 2.5, gap: 1 }}>
                    <Button onClick={closeReportDialog} disabled={reportSubmitting} variant="text"
                        sx={{
                            color: '#666', fontWeight: 600, fontSize: '0.91rem', borderRadius: 2, flex: 1, py: '11px',
                            bgcolor: '#f5f5f5',
                            '&:hover': { bgcolor: '#ececec' },
                        }}>{localize("취소")}</Button>
                    <Button onClick={submitReport} disabled={reportSubmitting} variant="contained"
                        sx={{
                            bgcolor: '#ffe566', color: '#5a4a00', fontWeight: 700,
                            fontSize: '0.91rem', borderRadius: 2, flex: 1, py: '11px',
                            boxShadow: 'none',
                            '&:hover': { bgcolor: '#e8d040', boxShadow: 'none' },
                            '&.Mui-disabled': { bgcolor: '#f0f0f0', color: '#bbb' },
                        }}>
                        {reportSubmitting ? <LoadingLabel /> : localize("신고하기")}
                    </Button>
                </DialogActions>
            </Dialog>

            <AppToast open={reportSnack.open} autoHideDuration={3000}
                onClose={() => setReportSnack(prev => ({ ...prev, open: false }))}
                severity={reportSnack.severity}
                message={localize(reportSnack.message)}
            />
        </Box>
    );
}
